import { Box, Checkbox, Chip, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { FunctionComponent, SetStateAction, useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import GroupService from '../../../shared/services/group/group.service';
import { IGroupWithCategory } from '../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../shared/constants/ghg-category';
import { ICategory } from '../../../shared/services/category/category.model';
import { ICreateReductionAction } from '../../../shared/services/reduction-action/reduction-action.model';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { getCategoryDisplayName } from '../../../shared/utils/category-utils';

export const AddCustomReductionActionDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  submitHandler: (values: ICreateReductionAction, formikResetForm: () => void, setSelectedCategoriesId: React.Dispatch<SetStateAction<any>>) => void,
  isSuperAdmin?: boolean,
  newReductionAction: ICreateReductionAction,
  isEdit?: boolean,
  isSubmitting: boolean,
}> = ({ open, closeHandler, submitHandler, isSuperAdmin, newReductionAction, isEdit = false, isSubmitting }) => {

  const groupService = new GroupService();
  const [groups, setGroups] = useState<IGroupWithCategory[]>()
  const [allowedCategories, setAllowedCategories] = useState<ICategory[]>()
  const [selectedCategoriesId, setSelectedCategoriesId] = useState<string[]>([])

  useEffect(() => {
    groupService.getGroupsWithCategories(false)
      .then(response => {
        setGroups(response)
        const group = response.find(group => group.id === newReductionAction.groupId)
        if (group) {
          setAllowedCategories(group.categories)
        }
      })
  }, [open, newReductionAction])

  useEffect(() => {
    formik.setValues({
      name: newReductionAction.name,
      description: newReductionAction.description,
      groupId: newReductionAction.groupId,
      categoryId: selectedCategoriesId,
      icon: newReductionAction.icon,
    })
  }, [newReductionAction])

  const customActionSchema = Yup.object().shape({
    name: Yup.string().required('You must provide a name'),
    description: Yup.string().nullable(),
    groupId: Yup.string().required('You must select a group'),
    categoryId: Yup.array().of(Yup.string()).min(1, 'You must select at least one category').required('You must select at least one category'),
    icon: Yup.string().nullable(),
  })

  const formik = useFormik({
    initialValues: {
      name: newReductionAction.name,
      description: newReductionAction.description,
      groupId: newReductionAction.groupId,
      categoryId: selectedCategoriesId,
      icon: newReductionAction.icon,
    },
    validationSchema: customActionSchema,
    onSubmit: (values) => {
      console.log(values)

      if (!isSuperAdmin) {
        const formattedValues = { ...values, isCustom: true }
        submitHandler(formattedValues, formik.resetForm, setSelectedCategoriesId)
      }
      if (isSuperAdmin) {
        const formattedValues = { ...values, isCustom: false }
        submitHandler(formattedValues, formik.resetForm, setSelectedCategoriesId)
      }
    }
  });

  const selectGroupHandler = (e: SelectChangeEvent) => {
    const oldGroup = groups?.find(group => group.id === formik.values.groupId)
    const newGroup = groups?.find(group => group.id === e.target.value)
    if (oldGroup?.id !== newGroup?.id) {
      formik.setFieldValue('groupId', newGroup?.id); console.log(newGroup?.categories)
      setAllowedCategories(newGroup?.categories)
      formik.setFieldValue('categoryId', '')
      setSelectedCategoriesId([])
    }
  }

  useEffect(() => {
    if (isEdit) {
      setSelectedCategoriesId(newReductionAction.categoryId)
    }
  }, [newReductionAction])

  const selectCategoryHandler = (e: SelectChangeEvent<string[]>) => {
    const { value } = e.target;
    setSelectedCategoriesId(typeof value === 'string' ? value.split(',') : value,);
  }

  useEffect(() => {
    formik.setFieldValue('categoryId', selectedCategoriesId);
  }, [selectedCategoriesId]);


  const cancelHandler = () => {
    formik.resetForm();
    closeHandler();
    setSelectedCategoriesId([])
  }

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title={isSuperAdmin && !isEdit ? 'Super Admin: Add Reduction Action' : isSuperAdmin && isEdit ? 'Super Admin: Edit Reduction Action' : !isSuperAdmin && !isEdit ? 'Add Custom Reduction Action' : 'Edit Custom Reduction Action'}
      cancelHandler={cancelHandler}
      submitHandler={formik.submitForm}
      isSubmitting={isSubmitting}>
      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='name'>Name</InputLabel>
        <OutlinedInput id='name' name='name' label='Name'
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
        ></OutlinedInput>
        <Stack justifyContent='start' alignItems='start'>
          {formik.touched.name && formik.errors.name &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.name}</FormHelperText>
          }
        </Stack>
      </FormControl>
      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='description'>Description</InputLabel>
        <OutlinedInput id='description' name='description' label='Description'
          value={formik.values.description}
          onChange={formik.handleChange}
          error={formik.touched.description && Boolean(formik.errors.description)}
        ></OutlinedInput>
        <Stack justifyContent='start' alignItems='start'>
          {formik.touched.description && formik.errors.description &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.description}</FormHelperText>
          }
        </Stack>
      </FormControl>
      {groups &&
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='groupId'>Group</InputLabel>
          <Select id='groupId' name='groupId' label='Group'
            value={formik.values.groupId || ''}
            error={formik.touched.groupId && Boolean(formik.errors.groupId)}
            onChange={selectGroupHandler}
          >
            {sortGroupsByGhgCategory(groups).map((group, index) => (
              <MenuItem key={index} value={group.id}>{formatGroupLabel(group)}</MenuItem>
            ))}
          </Select>
          {formik.touched.groupId && formik.errors.groupId &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.groupId}</FormHelperText>
          }
        </FormControl>}
      {allowedCategories &&
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='categoryId'>Category</InputLabel>
          <Select id='categoryId' name='categoryId' label='Category'
            value={selectedCategoriesId}
            onChange={selectCategoryHandler}
            multiple
            error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value, index) => {
                  const selectedName = allowedCategories.find(category => category.id === value)?.name
                  return (
                    <Chip key={index} label={selectedName} />
                  )
                })}
              </Box>
            )}
            input={<OutlinedInput label="Category" />}
          >
            {allowedCategories.map((category, index) => (
              <MenuItem key={index} value={category.id}>
                <Checkbox checked={selectedCategoriesId?.includes(category.id)} />
                <ListItemText primary={getCategoryDisplayName(category)} />
              </MenuItem>
            ))}
          </Select>
          {formik.touched.categoryId && formik.errors.categoryId &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.categoryId}</FormHelperText>
          }
        </FormControl>}
      {!allowedCategories &&
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='categoryId'>Category</InputLabel>
          <Select id='categoryId' name='categoryId' label='Category'
            value={selectedCategoriesId}
            onChange={selectCategoryHandler}
            disabled
            input={<OutlinedInput label="Category" />}>
          </Select>
        </FormControl>}
      {isSuperAdmin &&
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='icon'>Associated Icon</InputLabel>
          <Select id='icon' name='icon' label='Associated icon'
            value={formik.values.icon || ''}
            onChange={formik.handleChange}
            error={formik.touched.icon && Boolean(formik.errors.icon)}
          >
            {Object.values(IconName).map(icon => (
              <MenuItem key={icon} value={icon}>{icon.charAt(0).toUpperCase() + icon.slice(1)}</MenuItem>
            ))}
          </Select>
          <Typography variant='body2' sx={{ mt: 1 }}>If the icon you want does not appear it needs to be added to the icon picker interface and component</Typography>
          <Stack justifyContent='start' alignItems='start'>
            {formik.touched.icon && formik.errors.icon &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.icon}</FormHelperText>
            }
          </Stack>
        </FormControl>}
    </DialogWrapper>

  )
}